import _                                            from 'underscore';
import React, { FC, useContext, PropsWithChildren } from 'react';
import { useQuery }                                 from "@apollo/react-hooks";

import { formatOption, IFormatOptionValue }                                             from '@src/util';
import { CorvalMilestone, CorvalProjectVersionMilestone, PreSelectedSelectField, Uuid } from '@src/types';
import { queriesHaveData, queriesLoading }                                              from "@src/store/_apolloUtil";
import { ProjectQueries }                                                               from "@src/queries/ProjectQueries";
import { ProjectContext }                                                               from "@src/views/pages/projects/ProjectIndexPage";
import { projectMilestoneFields, projectVersionMilestoneFields }                        from "@src/constants";
import { SelectField }                                                                  from '../../../components/forms';


interface Props extends PropsWithChildren<any>, PreSelectedSelectField {
    projectId?: Uuid,
    projectVersionId?: Uuid,
    includeOwnMilestones?: boolean,
    includeSystemMilestones?: boolean,
    dedupeByName?: boolean,
    dedupeByNameAndDescription?: boolean,
}

type OptionObject = Pick<CorvalProjectVersionMilestone, "id" | "name" | "description" | "milestoneDate" | "milestoneId" | "colorValue" | "iconName"> & {
    projectVersionName?: string
    group?: string
}

const defaultProps: Props = {
    projectId:                  null,
    projectVersionId:           null,
    includeOwnMilestones:       true,
    includeSystemMilestones:    true,
    dedupeByName:               true,
    dedupeByNameAndDescription: true,
}

const Dropdown: FC<Props> = ( _props ) => {

          let projectContext = useContext( ProjectContext );

          const { projectId: contextProjectId, projectVersionId: contextProjectVersionId } = projectContext;
          const {
                    projectId:        propsProjectId,
                    projectVersionId: propsProjectVersionId,
                    children,
                    includeSystemMilestones,
                    includeOwnMilestones,
                    dedupeByName,
                    dedupeByNameAndDescription,
                    ...props
                } = _props;

          const projectId        = propsProjectId ?? contextProjectId ?? null,
                projectVersionId = propsProjectVersionId ?? contextProjectVersionId ?? null

          if( !projectId || !projectVersionId ) return null;

          const getProjectVersionMilestonesReq = useQuery(
              ProjectQueries.queries.GET_PROJECT( `
                    milestones{
                        ${ projectMilestoneFields }
                    }
                    projectVersions {
                        id
                        name
                        projectVersionMilestones {
                            projectVersionId 
                            ${ projectVersionMilestoneFields }
                        }
                    }
                ` ), {
                  variables:   {
                      id: projectId
                  },
                  fetchPolicy: 'cache-and-network'
              } );

          const isLoading = ( props.loading || queriesLoading( getProjectVersionMilestonesReq ) );

          let options        = [] as IFormatOptionValue[],
              optionsObjects = [] as OptionObject[];

          if( queriesHaveData( getProjectVersionMilestonesReq ) ){

              const project                  = _.first( getProjectVersionMilestonesReq.data.projects ),
                    systemMilestones         = project.milestones,
                    projectVersions          = includeOwnMilestones ? project?.projectVersions : _.filter( project?.projectVersions, projectVersion => ( projectVersion.id !== projectVersionId ) ),
                    projectVersionMilestones = _.chain( projectVersions )
                                                .pluck( 'projectVersionMilestones' )
                                                .flatten()
                                                .map( projectVersionMilestone => {
                                                    return {
                                                        ...projectVersionMilestone,
                                                        projectVersionName: _.findWhere( projectVersions, { id: projectVersionMilestone.projectVersionId } )?.name ?? 'Other Versions',
                                                    } as OptionObject
                                                } )
                                                .value();


              _.sortBy( systemMilestones, 'name' ).map( ( systemMilestone: CorvalMilestone ) => {

                  const { id, name, description, colorValue, iconName } = systemMilestone

                  optionsObjects.push( {
                      id,
                      name,
                      iconName,
                      colorValue,
                      description,
                      milestoneId:   id,
                      milestoneDate: null,
                      group:         'System'
                  } )

                  options.push(
                      // @ts-ignore
                      formatOption( id, name, { group: 'System', description } ),
                  );
              } );

              _.sortBy( projectVersionMilestones, 'name' ).map( projectVersionMilestone => {

                  const { id, name, iconName, colorValue, description, milestoneDate, milestoneId, projectVersionName } = projectVersionMilestone;

                  optionsObjects.push( {
                      id,
                      name,
                      iconName,
                      colorValue,
                      description,
                      milestoneDate,
                      milestoneId,
                      group: projectVersionName
                  } );

                  options.push(
                      // @ts-ignore
                      formatOption( id, name, { group: projectVersionName, description } ),
                  );
              } )
          }

          if( dedupeByName || dedupeByNameAndDescription ){
              optionsObjects = _.uniq( optionsObjects, optionsObject => `${ optionsObject.name }|${ dedupeByNameAndDescription ? optionsObject.description : '' }` )
              options = _.uniq( options, option => `${ option.label }|${ dedupeByNameAndDescription ? option.description : '' }` )
          }

          if( props.only ){

              optionsObjects = _.filter( optionsObjects, optionsObject => props.only.includes( optionsObject.id ) )
              options = _.filter( options, option => props.only.includes( option.value ) )
          }
          if( props.omit ){

              optionsObjects = _.reject( optionsObjects, optionsObject => props.omit.includes( optionsObject.id ) )
              options = _.reject( options, option => props.omit.includes( option.value ) )
          }

          if( children ){

              if( queriesHaveData( getProjectVersionMilestonesReq ) ){

                  return _.isFunction( children )
                         ? children( optionsObjects )
                         : children;
              } else {

                  return null;
              }
          }

          return (
              <SelectField
                  { ...props }
                  loading={ isLoading }
                  options={ options }>
              </SelectField>
          );
      }
;

Dropdown.defaultProps = defaultProps;

export {
    Dropdown as AllProjectVersionMilestonesDropdown
};
